import type { DefaultPaginatedFilters } from "@/api/shared/filters";

export type TPracticeQrStatus = "active" | "inactive";
export type TPracticeQrTreatmentType =
  | "cotreatment"
  | "referral"
  | "referral_free"
  | "referral_paid"
  | "select_treatment"
  | "referral_internal";

export const PRACTICE_QR_TREATMENT_TYPE_VALUES = [
  "cotreatment",
  "referral",
  "referral_free",
  "referral_paid",
  "select_treatment",
] as const satisfies readonly TPracticeQrTreatmentType[];

export type TPracticeQrOwnerStaffRef =
  | string
  | {
      id: string;
      first_name?: string;
      last_name?: string;
    };

export const getPracticeQrOwnerStaffId = (ref: TPracticeQrOwnerStaffRef): string =>
  typeof ref === "string" ? ref : ref.id;

export type TPracticeQrToPracticeRef =
  | string
  | {
      id: string;
      name?: string;
    };

export const getPracticeQrToPracticeId = (ref: TPracticeQrToPracticeRef | undefined): string => {
  if (ref == null) return "";
  return typeof ref === "string" ? ref.trim() : ref.id;
};

export type TPracticeQrEventData = {
  campaignSource?: string;
  campaignName?: string;
  distributorName?: string;
};

export type TPracticeQr = {
  id: string;
  short_code?: string;
  practice: string;
  usages_count?: number;
  treatment_type: TPracticeQrTreatmentType;
  is_direct: boolean;
  treatment_data: {
    to_practice?: TPracticeQrToPracticeRef;
  };
  event: TPracticeQrEventData;
  owner_staff: TPracticeQrOwnerStaffRef;
  creator_staff?: TPracticeQrOwnerStaffRef;
  url?: string;
  redirector_url?: string;
  qr_url?: string;
  created_at?: string;
  status?: TPracticeQrStatus;
};

export type TPracticeQrUsage = {
  created_at: string;
  practice_qr: {
    id: string;
    practice?: string;
    short_code: string;
    url: string | null;
    is_direct: boolean;
    treatment_type?: TPracticeQrTreatmentType;
    event: TPracticeQrEventData | null;
    description: string | null;
    created_at: string;
    creator_staff?: TPracticeQrOwnerStaffRef | null;
    owner_staff?: TPracticeQrOwnerStaffRef | null;
  } | null;
};

export const hasPracticeQrToPractice = (item: Pick<TPracticeQr, "treatment_data">): boolean =>
  Boolean(getPracticeQrToPracticeId(item.treatment_data?.to_practice));

export type TPracticeQrCreatePayload = {
  practice: string;
  treatment_type: TPracticeQrTreatmentType;
  is_direct: boolean;
  treatment_data: {
    to_practice?: string;
  };
  event: TPracticeQrEventData;
  owner_staff: string;
};

export type IPracticeQrFilter = DefaultPaginatedFilters & {
  practice?: string;
  participatory_practice?: string;
  creator_staff?: string;
  event?: string;
  is_direct?: boolean;
  status?: TPracticeQrStatus;
  treatment_type?: TPracticeQrTreatmentType;
  treatment_type__in?: TPracticeQrTreatmentType[];
  to_practice?: string;
};
